/**
 * DeleteItemDialog - Confirmation dialog for deleting list items
 *
 * Shows warning when item has connections (edges) that will be lost.
 *
 * Sprint 6: Add/Delete Items
 */

import React from 'react';
import { Typography, Box, Alert } from '@mui/material';
import { SDialog } from '@modules/ui-components';

/**
 * Props for DeleteItemDialog
 */
export interface DeleteItemDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Item index being deleted (for display) */
  itemIndex: number;

  /** Item title for display */
  itemTitle: string;

  /** Whether item has connections */
  hasConnections: boolean;

  /** Callback when confirmed */
  onConfirm: () => void;

  /** Callback when cancelled */
  onCancel: () => void;
}

/**
 * DeleteItemDialog - Confirmation for item deletion
 */
export const DeleteItemDialog: React.FC<DeleteItemDialogProps> = ({
  open,
  itemIndex,
  itemTitle,
  hasConnections,
  onConfirm,
  onCancel,
}) => {
  const title = `Delete Item ${itemIndex + 1}?`;
  const displayTitle = itemTitle || `Item ${itemIndex + 1}`;

  const message = hasConnections
    ? `"${displayTitle}" has connections to other screens. Deleting this item will remove those connections.`
    : `Are you sure you want to delete "${displayTitle}"?`;

  return (
    <SDialog
      open={open}
      onClose={onCancel}
      title={title}
      maxWidth="xs"
      fullWidth
      // Destructive confirmation — explicit footer Cancel, no header X.
      showCloseButton={false}
      primaryAction={{
        label: 'Delete',
        onClick: onConfirm,
        color: 'error',
        variant: 'contained',
        'data-testid': 'delete-confirm-button',
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: onCancel,
        variant: 'outlined',
        'data-testid': 'delete-cancel-button',
      }}
      data-testid="delete-item-dialog"
    >
      <Box sx={{ py: 1 }}>
        {hasConnections && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This item has connections that will be lost.
          </Alert>
        )}
        <Typography variant="body2">{message}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          This action cannot be undone.
        </Typography>
      </Box>
    </SDialog>
  );
};

export default DeleteItemDialog;
